import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import Modal, { ModalBody, ModalFooter } from '../modal/modal';
import { FormControlTextarea } from '../form/FormControlTextarea';
import { InlineSpinner } from '../presentational/spinner';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import {
  ModerationCategory,
  ModerationSubReason,
  ICategoryDefinition,
  CATEGORY_DEFINITIONS,
  CATEGORY_ACCENT_COLORS,
  SUB_REASON_ICONS,
  categoryLabelKey,
  categoryDescriptionKey,
  subReasonLabelKey,
  subReasonDescriptionKey,
  isDescriptionRequired
} from '../../../common/moderationReasonsConfig';
import { useModerationReport, ISubmitModerationReportParams } from './useModerationReport';
import { useIsAuthenticated, useSiteConfig } from '../../scripts/selectors/global';
import { ComplexTooltip } from '../presentational/tooltip';
import {
  RECAPTCHA_ENTERPRISE_SITE_KEY,
  TENCENT_CAPTCHA_APP_ID,
  shouldUseTencentCaptcha
} from '../../../common/captchaConfig';

// Module-level state: persist reCAPTCHA across modal opens to avoid re-render crashes
let recaptchaScriptLoadedGlobal = false;
let tencentScriptLoadedGlobal = false;
let recaptchaWidgetIdGlobal: number | null = null;
let recaptchaContainerGlobal: HTMLDivElement | null = null;

function ReportContextInfo(props: {
  contextType: ModerationReportContextType;
  entityId: number;
  entityName?: string;
  sourceUrl?: string;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const pageUrl = location.pathname + location.search;
  return (
    <ComplexTooltip
      placement="top"
      tooltip={
        <div className="slds-text-body--small" style={{ lineHeight: 1.5, maxWidth: 320 }}>
          <div className="slds-m-bottom--xx-small" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {t('moderation:modal.context-info-heading')}
          </div>
          <div>
            <b>{t('moderation:modal.context-info-type')}:</b> {props.contextType}
          </div>
          <div>
            <b>{t('moderation:modal.context-info-entity-id')}:</b>{' '}
            {props.entityId || t('moderation:modal.context-info-none')}
          </div>
          {props.entityName && (
            <div>
              <b>{t('moderation:modal.context-info-entity')}:</b> {props.entityName}
            </div>
          )}
          {props.sourceUrl && (
            <div>
              <b>{t('moderation:modal.context-info-source')}:</b> {props.sourceUrl}
            </div>
          )}
          <div>
            <b>{t('moderation:modal.context-info-page')}:</b> {pageUrl}
          </div>
        </div>
      }
    >
      <i className="fa fa-info-circle slds-text-body--small text-muted" style={{ cursor: 'help' }} />
    </ComplexTooltip>
  );
}

function ReasonCard(props: {
  icon: string;
  label: string;
  description: string;
  accentColor: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="moderation-report-modal__reason-card moderation-report-modal__reason-card--accented"
      style={{ '--moderation-accent-color': props.accentColor } as React.CSSProperties}
      onClick={props.onClick}
    >
      <div className="moderation-report-modal__reason-card-icon moderation-report-modal__reason-card-icon--tinted">
        <i className={`fa-solid ${props.icon}`} />
      </div>
      <div className="moderation-report-modal__reason-card-content">
        <div className="moderation-report-modal__reason-card-label">{props.label}</div>
        <div className="moderation-report-modal__reason-card-description">{props.description}</div>
      </div>
      <div className="moderation-report-modal__reason-card-arrow">
        <i className="fa-solid fa-chevron-right" />
      </div>
    </button>
  );
}

function SelectionSummary(props: { categoryDef: ICategoryDefinition; selectedSubReason?: ModerationSubReason | null }) {
  const { t } = useTranslation();
  const { categoryDef, selectedSubReason } = props;
  const accentColor = CATEGORY_ACCENT_COLORS[categoryDef.category];
  return (
    <div
      className="moderation-report-modal__selected-summary moderation-report-modal__selected-summary--accented"
      style={{ '--moderation-accent-color': accentColor } as React.CSSProperties}
    >
      <div className="moderation-report-modal__selected-summary-category">
        <i className={`fa-solid ${categoryDef.icon}`} />
        <span>{t(categoryLabelKey(categoryDef.category))}</span>
      </div>
      {selectedSubReason && (
        <>
          <div className="moderation-report-modal__selected-summary-separator">
            <i className="fa-solid fa-chevron-right" />
          </div>
          <div className="moderation-report-modal__selected-summary-sub-reason">
            {t(subReasonLabelKey(selectedSubReason))}
          </div>
        </>
      )}
    </div>
  );
}

interface IModerationReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextType: ModerationReportContextType;
  entityId: number;
  entityName?: string;
  sourceUrl?: string;
}

type Step = 'category' | 'sub-reason' | 'details';

export function ModerationReportModal(props: IModerationReportModalProps) {
  const { isOpen, onClose, contextType, entityId, entityName, sourceUrl } = props;
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const siteConfig = useSiteConfig();
  const isCN = shouldUseTencentCaptcha(siteConfig?.country || '');

  const { submitReport, reset, setError, state, referenceCode, error, retryAfterSeconds } = useModerationReport();

  const [selectedCategory, setSelectedCategory] = useState<ModerationCategory | null>(null);
  const [selectedSubReason, setSelectedSubReason] = useState<ModerationSubReason | null>(null);
  const [description, setDescription] = useState('');
  const [step, setStep] = useState<Step>('category');
  const [captchaScriptLoaded, setCaptchaScriptLoaded] = useState(
    isCN ? tencentScriptLoadedGlobal : recaptchaScriptLoadedGlobal
  );

  const pendingSubmitParams = useRef<ISubmitModerationReportParams | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(null);
      setSelectedSubReason(null);
      setDescription('');
      setStep('category');
      reset();
    }
  }, [isOpen, reset]);

  // Load captcha script once (persists across modal opens)
  useEffect(() => {
    if (isAuthenticated || !isOpen) {
      return;
    }

    if (isCN) {
      if (tencentScriptLoadedGlobal) {
        setCaptchaScriptLoaded(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://ca.turing.captcha.qcloud.com/TCaptcha-global.js';
      script.onload = () => {
        tencentScriptLoadedGlobal = true;
        setCaptchaScriptLoaded(true);
      };
      document.body.appendChild(script);
    } else {
      if (recaptchaScriptLoadedGlobal) {
        setCaptchaScriptLoaded(true);
        return;
      }
      (window as any).rioModerationCaptchaReady = () => {
        recaptchaScriptLoadedGlobal = true;
        setCaptchaScriptLoaded(true);
      };
      const script = document.createElement('script');
      script.src = 'https://www.recaptcha.net/recaptcha/enterprise.js?onload=rioModerationCaptchaReady&render=explicit';
      document.body.appendChild(script);
    }
    // No cleanup: scripts persist across modal opens to avoid re-render crashes
  }, [isOpen, isAuthenticated, isCN]);

  // Render invisible reCAPTCHA widget into a persistent DOM element (outside React's tree).
  // This avoids React's reconciliation unmounting/remounting the container and destroying the widget.
  useEffect(() => {
    if (!captchaScriptLoaded || isCN || isAuthenticated) {
      return;
    }

    const grecaptcha = (window as any).grecaptcha?.enterprise;
    if (!grecaptcha) {
      return;
    }

    // Widget already exists — just reset it
    if (recaptchaWidgetIdGlobal != null) {
      grecaptcha.reset(recaptchaWidgetIdGlobal);
      return;
    }

    // Create a persistent container outside React's DOM tree
    if (!recaptchaContainerGlobal) {
      recaptchaContainerGlobal = document.createElement('div');
      document.body.appendChild(recaptchaContainerGlobal);
    }

    recaptchaWidgetIdGlobal = grecaptcha.render(recaptchaContainerGlobal, {
      sitekey: RECAPTCHA_ENTERPRISE_SITE_KEY,
      size: 'invisible',
      callback: (token: string) => {
        const params = pendingSubmitParams.current;
        if (params) {
          pendingSubmitParams.current = null;
          submitReport({ ...params, recaptchaCode: token });
        }
      },
      'error-callback': () => {
        pendingSubmitParams.current = null;
        setError('captcha_invalid');
      },
      'expired-callback': () => {
        pendingSubmitParams.current = null;
        setError('captcha_invalid');
      }
    });
  }, [captchaScriptLoaded, isCN, isAuthenticated, submitReport, setError]);

  const selectedCategoryDef = selectedCategory
    ? CATEGORY_DEFINITIONS.find((d) => d.category === selectedCategory)
    : null;

  const handleSelectCategory = useCallback((category: ModerationCategory) => {
    setSelectedCategory(category);
    const def = CATEGORY_DEFINITIONS.find((d) => d.category === category);
    if (def && def.subReasons.length === 1) {
      // Skip sub-reason step when only one option (e.g. Feature Request)
      setSelectedSubReason(def.subReasons[0]);
      setStep('details');
    } else {
      setSelectedSubReason(null);
      setStep('sub-reason');
    }
  }, []);

  const handleSelectSubReason = useCallback((subReason: ModerationSubReason) => {
    setSelectedSubReason(subReason);
    setStep('details');
  }, []);

  const handleBack = useCallback(() => {
    reset();
    setDescription('');
    setSelectedSubReason(null);
    // Go back to category if we're on sub-reason, or if details was reached
    // by skipping sub-reason (single sub-reason category)
    const skipSubReason = selectedCategoryDef && selectedCategoryDef.subReasons.length === 1;
    if (step === 'sub-reason' || (step === 'details' && skipSubReason)) {
      setSelectedCategory(null);
      setStep('category');
    } else {
      setStep('sub-reason');
    }
  }, [step, selectedCategoryDef, reset]);

  const handleSubmit = useCallback(async () => {
    if (!selectedCategory || !selectedSubReason) {
      return;
    }

    if (isDescriptionRequired(selectedSubReason) && !description.trim()) {
      return;
    }

    const baseParams: ISubmitModerationReportParams = {
      contextType,
      contextEntityId: entityId,
      reason: selectedCategory,
      subReason: selectedSubReason,
      description: description.trim() || undefined,
      sourceUrl:
        sourceUrl || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined)
    };

    // Authenticated users skip captcha
    if (isAuthenticated) {
      await submitReport(baseParams);
      return;
    }

    // CN users: Tencent CAPTCHA popup
    if (isCN) {
      const { TencentCaptcha } = window as any;
      if (!TencentCaptcha) {
        setError('captcha_invalid');
        return;
      }

      try {
        const captcha = new TencentCaptcha(
          String(TENCENT_CAPTCHA_APP_ID),
          (res: any) => {
            if (res.ret === 0) {
              submitReport({
                ...baseParams,
                tencentTicket: res.ticket,
                tencentRandstr: res.randstr
              });
            }
          },
          { enableDarkMode: true }
        );
        captcha.show();
      } catch (_err) {
        setError('captcha_invalid');
      }
      return;
    }

    // Non-CN users: invisible reCAPTCHA Enterprise
    const grecaptcha = (window as any).grecaptcha?.enterprise;
    if (!grecaptcha || recaptchaWidgetIdGlobal == null) {
      setError('captcha_invalid');
      return;
    }
    pendingSubmitParams.current = baseParams;
    grecaptcha.execute(recaptchaWidgetIdGlobal);
  }, [
    selectedCategory,
    selectedSubReason,
    description,
    contextType,
    entityId,
    sourceUrl,
    submitReport,
    setError,
    isAuthenticated,
    isCN
  ]);

  const errorMessage = !error
    ? null
    : error === 'rate_limit'
      ? t('moderation:errors.rate-limit', { minutes: Math.ceil((retryAfterSeconds || 3600) / 60) })
      : t(`moderation:errors.${error.replace(/_/g, '-')}`, { defaultValue: t('moderation:errors.generic') });

  const title = entityName ? t('moderation:modal.title-with-name', { name: entityName }) : t('moderation:modal.title');

  const isSubmitDisabled =
    state === 'loading' ||
    !selectedCategory ||
    !selectedSubReason ||
    (isDescriptionRequired(selectedSubReason!) && !description.trim());

  const contextInfoProps = { contextType, entityId, entityName, sourceUrl };

  // Success state
  if (state === 'success' && referenceCode) {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose} title={t('moderation:success.title')}>
        <ModalBody>
          <div className="moderation-report-modal__success">
            <div className="moderation-report-modal__success-icon">
              <i className="fa fa-check-circle" />
            </div>
            <p className="moderation-report-modal__success-message">{t('moderation:success.message')}</p>
            <p className="moderation-report-modal__success-reference">
              <strong>{t('moderation:success.reference', { code: referenceCode })}</strong>
            </p>
            <p className="moderation-report-modal__success-note">{t('moderation:success.reference-note')}</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="slds-button slds-button--brand" onClick={onClose}>
            {t('moderation:success.close')}
          </button>
        </ModalFooter>
      </Modal>
    );
  }

  // Screen 1: Category selection
  if (step === 'category') {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose} title={title}>
        <ModalBody>
          <div className="moderation-report-modal__form">
            <div className="moderation-report-modal__reason-cards">
              {CATEGORY_DEFINITIONS.map((def) => (
                <ReasonCard
                  key={def.category}
                  icon={def.icon}
                  label={t(categoryLabelKey(def.category))}
                  description={t(categoryDescriptionKey(def.category))}
                  accentColor={CATEGORY_ACCENT_COLORS[def.category]}
                  onClick={() => handleSelectCategory(def.category)}
                />
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="moderation-report-modal__footer--split">
            <ReportContextInfo {...contextInfoProps} />
            <button type="button" className="slds-button slds-button--neutral" onClick={onClose}>
              {t('moderation:modal.cancel')}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  // Screen 2: Sub-reason selection
  if (step === 'sub-reason' && selectedCategoryDef) {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose} title={title}>
        <ModalBody>
          <div className="moderation-report-modal__form">
            <SelectionSummary categoryDef={selectedCategoryDef} />

            <p className="moderation-report-modal__subtitle">{t('moderation:modal.subtitle-select-sub-reason')}</p>
            <div className="moderation-report-modal__reason-cards">
              {selectedCategoryDef.subReasons.map((subReason) => (
                <ReasonCard
                  key={subReason}
                  icon={SUB_REASON_ICONS[subReason]}
                  label={t(subReasonLabelKey(subReason))}
                  description={t(subReasonDescriptionKey(subReason))}
                  accentColor={CATEGORY_ACCENT_COLORS[selectedCategoryDef.category]}
                  onClick={() => handleSelectSubReason(subReason)}
                />
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="moderation-report-modal__footer--split">
            <ReportContextInfo {...contextInfoProps} />
            <button type="button" className="slds-button slds-button--neutral" onClick={handleBack}>
              {t('moderation:modal.back')}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  // Screen 3: Details form
  const descriptionRequired = selectedSubReason ? isDescriptionRequired(selectedSubReason) : false;

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} title={t('moderation:modal.title-details')}>
      <ModalBody>
        <div className="moderation-report-modal__form">
          {selectedCategoryDef && selectedSubReason && (
            <SelectionSummary categoryDef={selectedCategoryDef} selectedSubReason={selectedSubReason} />
          )}

          {/* Description textarea */}
          <div className="moderation-report-modal__field">
            <label className="moderation-report-modal__label">
              {t('moderation:modal.description-label')}
              {descriptionRequired && <span className="moderation-report-modal__required"> *</span>}
            </label>
            <FormControlTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('moderation:modal.description-placeholder')}
              rows={4}
              maxLength={2000}
              variant="rio"
            />
            <p
              className="moderation-report-modal__hint"
              style={{ visibility: descriptionRequired && !description.trim() ? 'visible' : 'hidden' }}
            >
              {t('moderation:modal.description-required')}
            </p>
          </div>

          {/* Error message */}
          {state === 'error' && errorMessage && (
            <div className="moderation-report-modal__error">
              <i className="fa fa-exclamation-triangle" />
              <span>{errorMessage}</span>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="moderation-report-modal__footer--split">
          <ReportContextInfo {...contextInfoProps} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="slds-button slds-button--neutral" onClick={handleBack}>
              {t('moderation:modal.back')}
            </button>
            <button
              type="button"
              className="slds-button slds-button--brand"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {state === 'loading' ? (
                <InlineSpinner className="moderation-report-modal__spinner" />
              ) : (
                t('moderation:modal.submit')
              )}
            </button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
